import * as React from 'react';
import * as queryString from 'query-string';
import { useParams } from 'react-router-dom';
import Loading from '@doordash/component-loading';
import ChooseAccountView from './ChooseAccountView';
import { useGetConsumerInfo } from '../../core/DynamicValues/hook';
import LocationPathHelper from '../../utils/location_path_helper';
import { EXTERNAL_ROUTES, HOME_PAGE_PATH, IDENTITY_SERVICE_POST_LOGOUT_PATH, PORTAL_ROUTE_PREFIX, } from '../../constants/values';
import { getIdentityUrl, DDFB_IDENTITY_LAYOUTS, getLogoutUrl, } from '../../utils/identity_helper';
import { useGetTeamNameByPendingMemberId } from '../../hooks/useGetTeamNameByPendingMemberId';
import { QUERY_PARAMS } from '../../constants/queryParams';
import useJoinByInvite from '../../hooks/useJoinByInvite';
import { ERRORS } from './constants';
import JoinByInviteErrorPage from './JoinByInviteErrorPage';
import { JoinByInviteTestId } from '../../core/Common/testId';
import track from '../../utils/track';
import { JoinV2PageEvents } from '../../constants/events/names';
import { useTeamData } from '../../hooks/useTeamData';
/*
  This component covers:
    1a. Redirect to identity page to sign in/up for guest users
    1b. Redirect back to join page to automatically activate benefits and
        redirect to Benefits page

    2a. Display ChooseAccountView to allow logged in user to use current
        account or choose another account (redirect to Identity page).
    2b. After selecting the account, this will automatically activate benefits
        and redirect to Benefits page.

    3. If user already has an active membership, redirect to DD Benefits page
*/
var JoinByInviteContainer = function () {
    var _a, _b, _c;
    var joinTeamAccountsCalled = React.useRef(false);
    var searchParams = queryString.parse(LocationPathHelper.getWindowLocationSearch());
    var isIdentitylogInSuccess = (searchParams === null || searchParams === void 0 ? void 0 : searchParams.loginSuccess) === 'true';
    var pendingMemberId = useParams().pendingMemberId;
    var _d = useGetConsumerInfo(), isGuest = _d.isGuest, consumerInfoLoading = _d.loading, error = _d.error;
    var _e = useTeamData({
        skip: isGuest || consumerInfoLoading,
    }), teamData = _e.data, teamDataLoading = _e.loading;
    var shouldSkipJoin = consumerInfoLoading ||
        isGuest ||
        teamDataLoading ||
        !!((_b = (_a = teamData === null || teamData === void 0 ? void 0 : teamData.teamData) === null || _a === void 0 ? void 0 : _a.team) === null || _b === void 0 ? void 0 : _b.id); // should complete calls and skip join if guest or already part of team
    var _f = useGetTeamNameByPendingMemberId({
        invitationCode: pendingMemberId,
        forceSkip: shouldSkipJoin,
    }), getTeamNameData = _f.data, isGetTeamNameLoading = _f.loading;
    var pendingMemberTeamName = (_c = getTeamNameData === null || getTeamNameData === void 0 ? void 0 : getTeamNameData.getTeamName) === null || _c === void 0 ? void 0 : _c.teamName;
    var _g = useJoinByInvite(pendingMemberId, shouldSkipJoin), joinByInvite = _g.joinByInvite, errorType = _g.errorType, getJoinContextLoading = _g.getJoinContextLoading, getJoinContextData = _g.getJoinContextData, joinTeamAccountsV2Loading = _g.joinTeamAccountsV2Loading, joinTeamAccountsV2Data = _g.joinTeamAccountsV2Data;
    // any data loading state before calling joinTeamAccountsV2
    var isAnyDataLoading = consumerInfoLoading ||
        isGetTeamNameLoading ||
        getJoinContextLoading ||
        teamDataLoading;
    var getLogInUrl = React.useCallback(function () {
        var currentSearch = LocationPathHelper.getWindowLocationSearch();
        var nextPathQueryString = currentSearch.length > 0
            ? currentSearch + "&" + QUERY_PARAMS.LOGIN_SUCCESS + "=true"
            : "?" + QUERY_PARAMS.LOGIN_SUCCESS + "=true";
        var nextPath = "" + (LocationPathHelper.getPathName() || HOME_PAGE_PATH) + nextPathQueryString;
        return getIdentityUrl({ nextPath: nextPath }, {
            identityMode: 'login',
        }, { companyName: pendingMemberTeamName }, DDFB_IDENTITY_LAYOUTS.BENEFIT_ACTIVATION);
    }, [pendingMemberTeamName]);
    var logoutUserAndRedirectBackToJoinPage = React.useCallback(function () {
        var postLogoutRedirectPath = IDENTITY_SERVICE_POST_LOGOUT_PATH + "/?state=" + PORTAL_ROUTE_PREFIX + "/join/" + pendingMemberId;
        window.location.href = getLogoutUrl(postLogoutRedirectPath);
    }, [pendingMemberId]);
    React.useEffect(function () {
        if (!consumerInfoLoading) {
            track(JoinV2PageEvents.WORK_PORTAL_JOIN_PAGE_V2_PAGE_LOAD, {
                pending_member_id: pendingMemberId,
                is_guest: isGuest,
            });
        }
    }, [consumerInfoLoading, pendingMemberId, isGuest]);
    // Case 1a
    React.useEffect(function () {
        if ((!consumerInfoLoading &&
            isGuest &&
            !isGetTeamNameLoading &&
            pendingMemberTeamName) ||
            error) {
            // redirect to identity page to sign in/up
            window.location.href = getLogInUrl();
        }
    }, [
        isGuest,
        consumerInfoLoading,
        error,
        isGetTeamNameLoading,
        pendingMemberTeamName,
        getLogInUrl,
    ]);
    // Case 1b
    React.useEffect(function () {
        var isPersonalLoginAllowed = getJoinContextData === null || getJoinContextData === void 0 ? void 0 : getJoinContextData.isPersonalLoginAllowed;
        if (!joinTeamAccountsCalled.current &&
            !getJoinContextLoading &&
            isPersonalLoginAllowed &&
            isIdentitylogInSuccess) {
            track(JoinV2PageEvents.WORK_PORTAL_JOIN_PAGE_V2_DDFB_IDENTITY_SUCCESS, {
                pending_member_id: pendingMemberId,
            });
            joinByInvite();
            joinTeamAccountsCalled.current = true;
        }
    }, [
        pendingMemberId,
        getJoinContextData,
        isIdentitylogInSuccess,
        getJoinContextLoading,
        joinByInvite,
    ]);
    // Case 3
    React.useEffect(function () {
        var _a, _b;
        if (!teamDataLoading && !!((_b = (_a = teamData === null || teamData === void 0 ? void 0 : teamData.teamData) === null || _a === void 0 ? void 0 : _a.team) === null || _b === void 0 ? void 0 : _b.id)) {
            // user already has an active membership should redirect to benefits page
            var benefitsPageWithJoin = EXTERNAL_ROUTES.BENEFITS_PAGE + "?joinV2=true";
            window.location.href = benefitsPageWithJoin;
        }
    }, [teamDataLoading, teamData]);
    // Error Case 1: invalid invite link - missing pending member id
    if (pendingMemberId === undefined) {
        return React.createElement(JoinByInviteErrorPage, { errorType: ERRORS.invalidInviteLink });
    }
    // Error Case 2: cannot find the team name - show generic error page
    if (!shouldSkipJoin &&
        !isGetTeamNameLoading &&
        pendingMemberTeamName === undefined) {
        return React.createElement(JoinByInviteErrorPage, { errorType: ERRORS.invalidInviteLink });
    }
    // Manage all JoinTeamAccountV2 errors
    if (!shouldSkipJoin &&
        !isAnyDataLoading &&
        !joinTeamAccountsV2Loading &&
        joinTeamAccountsV2Data &&
        errorType) {
        return (React.createElement(JoinByInviteErrorPage, { errorType: errorType, errorMetadata: joinTeamAccountsV2Data.error }));
    }
    // Error Case 4: Personal account not allowed, use company email
    // Log user out and redirect back to join page
    if (!shouldSkipJoin &&
        !isAnyDataLoading &&
        !isGuest &&
        !(getJoinContextData === null || getJoinContextData === void 0 ? void 0 : getJoinContextData.isPersonalLoginAllowed) &&
        !(getJoinContextData === null || getJoinContextData === void 0 ? void 0 : getJoinContextData.isConsumerEmailMatch)) {
        return (React.createElement(JoinByInviteErrorPage, { errorType: ERRORS.useCompanyEmail, consumerEmail: getJoinContextData === null || getJoinContextData === void 0 ? void 0 : getJoinContextData.consumerEmail }));
    }
    if (!shouldSkipJoin &&
        !isAnyDataLoading &&
        !joinTeamAccountsV2Loading &&
        !isGuest &&
        !isIdentitylogInSuccess &&
        errorType === null) {
        return (React.createElement(ChooseAccountView, { companyName: pendingMemberTeamName, joinTeamAccountsV2Loading: joinTeamAccountsV2Loading, joinTeamAccountsV2Data: joinTeamAccountsV2Data, joinByInvite: joinByInvite, chooseToUseAnotherAccount: function () {
                logoutUserAndRedirectBackToJoinPage();
            }, pendingMemberId: pendingMemberId }));
    }
    return (React.createElement(React.Fragment, null,
        React.createElement(Loading, { "data-testid": JoinByInviteTestId.PageLoadingSpinner, size: Loading.Sizes.Medium })));
};
export default JoinByInviteContainer;
